import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  authAPI,
  clearApiAccessToken,
  getApiAccessToken,
  hasRefreshSessionContext,
  setApiAccessToken,
} from '../api/client';

const AUTH_USE_COOKIE_REFRESH_RAW = (import.meta.env.VITE_AUTH_USE_COOKIE_REFRESH ?? 'true')
  .toString()
  .toLowerCase();
const AUTH_USE_COOKIE_REFRESH =
  AUTH_USE_COOKIE_REFRESH_RAW !== 'false' && AUTH_USE_COOKIE_REFRESH_RAW !== '0';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isDemo: false,
      isLoading: true,
      error: null,

      clearAuth: () => {
        clearApiAccessToken();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isDemo: false,
          isLoading: false,
        });
      },

      setTokens: (accessToken, refreshToken) => {
        setApiAccessToken(accessToken);
        set({
          accessToken,
          refreshToken: AUTH_USE_COOKIE_REFRESH ? null : refreshToken,
          isAuthenticated: !!accessToken,
        });
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login({ email, password });
          const { access_token, refresh_token } = response.data;
          setApiAccessToken(access_token);
          set({
            accessToken: access_token,
            refreshToken: AUTH_USE_COOKIE_REFRESH ? null : refresh_token,
            isAuthenticated: true,
            isDemo: false,
          });
          // Fetch user data after successful login
          try {
            const userResponse = await authAPI.me();
            set({ user: userResponse.data, isLoading: false });
          } catch (e) {
            console.error('Failed to fetch user after login:', e);
            set({ isLoading: false });
          }
          return true;
        } catch (error) {
          set({
            error: error.response?.data?.detail || 'Ошибка входа',
            isLoading: false,
          });
          return false;
        }
      },

      demoLogin: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.demoLogin();
          const { access_token, refresh_token } = response.data;
          setApiAccessToken(access_token);
          set({
            accessToken: access_token,
            refreshToken: AUTH_USE_COOKIE_REFRESH ? null : refresh_token,
            isAuthenticated: true,
            isDemo: true,
          });
          // Fetch user data after successful demo login
          try {
            const userResponse = await authAPI.me();
            set({ user: userResponse.data, isLoading: false });
          } catch (e) {
            console.error('Failed to fetch user after demo login:', e);
            set({ isLoading: false });
          }
          return true;
        } catch (error) {
          set({
            error: error.response?.data?.detail || 'Ошибка входа в демо-режим',
            isLoading: false,
          });
          return false;
        }
      },

      logout: async () => {
        try {
          await authAPI.logout(AUTH_USE_COOKIE_REFRESH ? undefined : get().refreshToken);
        } catch (e) {
          console.error('Logout error:', e);
        }
        get().clearAuth();
      },

      refreshAccessToken: async () => {
        try {
          const { accessToken, isAuthenticated, refreshToken, user } = get();
          if (
            !hasRefreshSessionContext({
              accessToken,
              isAuthenticated,
              refreshToken,
              user,
            })
          ) {
            return false;
          }

          const response = await authAPI.refresh(
            AUTH_USE_COOKIE_REFRESH ? undefined : refreshToken
          );
          const { access_token, refresh_token } = response.data;
          setApiAccessToken(access_token);
          set({
            accessToken: access_token,
            refreshToken: AUTH_USE_COOKIE_REFRESH ? null : refresh_token,
          });
          return true;
        } catch (error) {
          // Do not clear auth state here: initialize/login flows may run in parallel.
          return false;
        }
      },

      initializeAuth: async () => {
        const { isAuthenticated, refreshToken, user } = get();
        const accessToken = AUTH_USE_COOKIE_REFRESH ? getApiAccessToken() : get().accessToken;
        if (accessToken) {
          setApiAccessToken(accessToken);
        }
        const hasRefreshContext = hasRefreshSessionContext({
          accessToken,
          isAuthenticated,
          refreshToken,
          user,
        });
        const clearIfNoActiveAuth = () => {
          const state = get();
          const activeToken = state.accessToken || getApiAccessToken();
          if (activeToken) {
            set({ isLoading: false });
            return false;
          }
          get().clearAuth();
          set({ isLoading: false });
          return true;
        };
        const loadUser = async () => {
          const response = await authAPI.me();
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        };

        if (!accessToken) {
          if (!hasRefreshContext) {
            if (isAuthenticated || user) {
              get().clearAuth();
            } else {
              set({ isLoading: false });
            }
            return false;
          }

          set({ isLoading: true, error: null });
          const refreshed = await get().refreshAccessToken();
          if (!refreshed) {
            clearIfNoActiveAuth();
            return false;
          }
          return loadUser();
        }

        set({ isLoading: true, error: null });
        try {
          return await loadUser();
        } catch (error) {
          if (!AUTH_USE_COOKIE_REFRESH) {
            get().clearAuth();
            set({ isLoading: false });
            return false;
          }

          const refreshed = await get().refreshAccessToken();
          if (!refreshed) {
            clearIfNoActiveAuth();
            return false;
          }

          try {
            const response = await authAPI.me();
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          } catch (secondaryError) {
            console.error('Failed to fetch user after refresh:', secondaryError);
            clearIfNoActiveAuth();
            return false;
          }
        }
      },

      fetchUser: async () => {
        try {
          const response = await authAPI.me();
          set({ user: response.data });
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'letopis-auth',
      partialize: (state) => ({
        ...(AUTH_USE_COOKIE_REFRESH ? {} : { accessToken: state.accessToken }),
        ...(AUTH_USE_COOKIE_REFRESH ? {} : { refreshToken: state.refreshToken }),
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isDemo: state.isDemo,
      }),
    }
  )
);

